import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Upload, Play, Image as ImageIcon } from 'lucide-react';
import { reelApi } from '../../utils/api';

function AdminReels() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', video: null, poster: null, date: new Date().toISOString().slice(0, 10) });
  const [videoPreview, setVideoPreview] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);

  useEffect(() => {
    fetchReels();
  }, []);

  const fetchReels = async () => {
    setLoading(true);
    try {
      const response = await reelApi.getAll();
      setReels(response.data);
    } catch (error) {
      console.error('Error fetching reels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, video: file });
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handlePosterChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, poster: file });
      setPosterPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (!form.title) {
      toast.error('शीर्षक आवश्यक छ');
      return;
    }
    if (!form.video && !editing) {
      toast.error('भिडियो आवश्यक छ');
      return;
    }
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (key === 'video' && form[key]) formData.append('video', form[key]);
        else if (key === 'poster' && form[key]) formData.append('poster', form[key]);
        else if (key !== 'video' && key !== 'poster') formData.append(key, form[key]);
      });
      if (editing) {
        await reelApi.update(editing, formData);
        toast.success('रिल अद्यावधिक भयो।');
      } else {
        await reelApi.create(formData);
        toast.success('रिल थपियो।');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', video: null, poster: null, date: new Date().toISOString().slice(0, 10) });
      setVideoPreview(null);
      setPosterPreview(null);
      await fetchReels();
    } catch (error) {
      console.error('Error saving reel:', error);
      toast.error('भिडियो सुरक्षित गर्न असफल भयो। कृपया पुनः प्रयास गर्नुहोस्।');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('के तपाईं यो रिल मेटाउन निश्चित हुनुहुन्छ?')) return;
    try {
      await reelApi.delete(id);
      toast.success('रिल मेटियो।');
      await fetchReels();
    } catch (error) {
      console.error('Error deleting reel:', error);
      toast.error('रिल मेटाउन असफल भयो।');
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl">रिल्स</h2>
        <button
          className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-4 py-2.5 rounded-full hover:bg-crimson-dark"
          onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', video: null, poster: null, date: new Date().toISOString().slice(0, 10) }); setVideoPreview(null); setPosterPreview(null); }}
        >
          <Plus size={15} /> नयाँ थप्नुहोस्
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-line rounded-[14px] p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="md:col-span-2 flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              रिलको शीर्षक *
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="रिलको शीर्षक" />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              मिति
              <input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </label>
            <div className="md:col-span-2 flex flex-col gap-2.5">
              <span className="text-[13px] font-bold text-ink-soft">भिडियो अपलोड *</span>
              {videoPreview ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <video src={videoPreview} controls className="w-[220px] rounded-[10px] max-h-[200px]" />
                  <button type="button" className="text-[12.5px] font-semibold text-crimson hover:underline" onClick={() => { setVideoPreview(null); setForm({ ...form, video: null }); }}>
                    हटाउनुहोस्
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-dashed border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson w-fit">
                  <Upload size={14} /> भिडियो छान्नुहोस्
                  <input type="file" accept="video/*" hidden onChange={handleVideoChange} />
                </label>
              )}
            </div>
            <div className="md:col-span-2 flex flex-col gap-2.5">
              <span className="text-[13px] font-bold text-ink-soft">पोस्टर/थम्बनेल (वैकल्पिक)</span>
              {posterPreview ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <img src={posterPreview} alt="Poster preview" className="w-[120px] h-[90px] object-cover rounded-[10px]" />
                  <button type="button" className="text-[12.5px] font-semibold text-crimson hover:underline" onClick={() => { setPosterPreview(null); setForm({ ...form, poster: null }); }}>
                    हटाउनुहोस्
                  </button>
                </div>
              ) : (
                <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-dashed border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson w-fit">
                  <ImageIcon size={14} /> पोस्टर छान्नुहोस्
                  <input type="file" accept="image/*" hidden onChange={handlePosterChange} />
                </label>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2.5 mt-4">
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-line text-[13px] font-semibold text-ink-soft hover:bg-paper-soft" onClick={() => { setShowForm(false); setEditing(null); }}>
              रद्द गर्नुहोस्
            </button>
            <button className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark" onClick={handleSubmit}>
              {editing ? 'अद्यावधिक गर्नुहोस्' : 'थप्नुहोस्'}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {reels.map((reel) => (
          <div key={reel.id} className="flex items-center gap-3 bg-white border border-line rounded-xl p-3">
            <div className="relative w-[60px] h-20 rounded-lg overflow-hidden bg-ink flex-shrink-0">
              {(reel.poster || reel.video) && (
                <img
                  src={reel.poster || reel.video}
                  alt={reel.title}
                  className="w-full h-full object-cover"
                />
              )}
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white"><Play size={20} /></span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{reel.title}</p>
              <span className="text-xs text-muted">{reel.date || ''} • {reel.views || 0} पटक हेरिएको</span>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-paper-soft" onClick={() => { setEditing(reel.id); setForm({ title: reel.title, video: null, poster: null, date: reel.date ? String(reel.date).slice(0, 10) : new Date().toISOString().slice(0, 10) }); setVideoPreview(null); setPosterPreview(null); setShowForm(true); }} aria-label="Edit">
                <Pencil size={16} />
              </button>
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-red-50 hover:text-crimson" onClick={() => handleDelete(reel.id)} aria-label="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {reels.length === 0 && <p className="text-muted py-4">कुनै रिल्स छैन।</p>}
      </div>
    </div>
  );
}

export default AdminReels;